<template>
    <div class="recordings-manager">
        <!-- Header Section -->
        <div class="header-section">
            <v-card class="search-card">
                <v-card-text class="search-container">
                    <div class="search-and-count">
                        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="חיפוש" single-line
                            hide-details filled rounded dense class="search-field"
                            background-color="rgba(0, 0, 0, 0.03)" @input="handleSearchInput"></v-text-field>

                        <div class="records-count">
                            <v-chip color="#0d2c6d" text-color="white" small>
                                {{ totalItems.toLocaleString() }}
                            </v-chip>
                            <span class="count-label">הקלטות</span>
                        </div>
                    </div>

                    <div class="header-actions">
                        <!-- Added toggle view button -->
                        <v-btn-toggle v-model="viewMode" mandatory dense class="view-toggle">
                            <v-btn value="grid" small>
                                <v-icon small>mdi-view-grid</v-icon>
                            </v-btn>
                            <v-btn value="table" small>
                                <v-icon small>mdi-table</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <v-btn v-if="selectedRecords.length > 0" color="error" class="bulk-delete-btn" elevation="1"
                            @click="openBulkDeleteDialog">
                            <v-icon left>mdi-delete-sweep</v-icon>
                            מחק {{ selectedRecords.length }} פריטים
                        </v-btn>

                        <v-btn color="#0d2c6d" class="add-record-btn" elevation="1" @click="addRecordDialog = true">
                            <v-icon left>mdi-plus</v-icon>
                            הוסף הקלטה
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- Grid View Section -->
        <div v-if="viewMode === 'grid'" class="grid-section">
            <div v-if="progressShow" class="loading-container">
                <v-progress-circular indeterminate size="64" width="4" color="#0d2c6d"></v-progress-circular>
                <p class="loading-text">טוען נתונים...</p>
            </div>

            <div v-else-if="records.length === 0" class="no-data-container">
                <v-icon large color="#0d2c6d" class="mb-4">mdi-playlist-music-outline</v-icon>
                <div class="no-data-text">אין עדיין הקלטות</div>
                <v-btn color="#0d2c6d" outlined @click="refreshDetails()" class="refresh-btn mt-4">
                    <v-icon left>mdi-refresh</v-icon>
                    רענן
                </v-btn>
            </div>

            <div v-else class="recordings-grid">
                <v-card v-for="(item, index) in records" :key="item._id" class="recording-card" :class="{
                    'playing-card': item.isPlaying,
                    'selected-card': isSelected(item),
                }" elevation="2">
                    <!-- Improved card header with modern gradient and better contrast -->
                    <div class="card-header-modern">
                        <div class="checkbox-wrapper">
                            <v-checkbox :input-value="isSelected(item)" @change="toggleSelection(item)" color="white"
                                dark hide-details dense class="modern-checkbox"></v-checkbox>
                        </div>

                        <div class="card-id-modern">
                            <v-icon small color="white" class="audio-icon-modern">mdi-music-circle</v-icon>
                            <span class="id-number-modern">#{{ item.integer_id }}</span>
                            <v-icon v-if="item.isPlaying" small color="#4CAF50" class="playing-pulse-modern">
                                mdi-play-circle
                            </v-icon>
                        </div>
                    </div>

                    <!-- Cleaner card content with better spacing -->
                    <v-card-text class="card-content-modern">
                        <!-- Primary Info -->
                        <div class="primary-info-modern">
                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0" class="info-icon-modern">mdi-file-music-outline</v-icon>
                                <span class="info-label-modern">שם:</span>
                                <span class="info-value-modern text-truncate" :title="item.record_name_original">
                                    {{ item.record_name_original }}
                                </span>
                            </div>

                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0" class="info-icon-modern">mdi-phone-outline</v-icon>
                                <span class="info-label-modern">טלפון:</span>
                                <span class="info-value-modern">{{ item.caller_id }}</span>
                            </div>

                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0" class="info-icon-modern">mdi-calendar-outline</v-icon>
                                <span class="info-label-modern">הקלטה:</span>
                                <span class="info-value-modern">{{ item.createdAt }}</span>
                            </div>

                            <div class="info-row-modern">
                                <v-icon x-small color="#5c6bc0"
                                    class="info-icon-modern">mdi-clock-time-four-outline</v-icon>
                                <span class="info-label-modern">משך:</span>
                                <span class="info-value-modern">{{ item.record_duration }}</span>
                            </div>
                        </div>

                        <!-- Expandable Details -->
                        <v-expand-transition>
                            <div v-if="expandedCards[item._id]" class="expanded-details-modern">
                                <div class="info-row-modern">
                                    <v-icon x-small color="#7986cb"
                                        class="info-icon-modern">mdi-calendar-upload</v-icon>
                                    <span class="info-label-modern">העלאה:</span>
                                    <span class="info-value-modern">{{ item.customCreatedAt }}</span>
                                </div>

                                <div class="info-row-modern">
                                    <v-icon x-small color="#7986cb" class="info-icon-modern">mdi-tag-outline</v-icon>
                                    <span class="info-label-modern">סוג:</span>
                                    <span class="info-value-modern">{{ item.type }}</span>
                                </div>

                                <div class="info-row-modern" v-if="item.subscriber_id?.name">
                                    <v-icon x-small color="#7986cb"
                                        class="info-icon-modern">mdi-account-outline</v-icon>
                                    <span class="info-label-modern">שם:</span>
                                    <span class="info-value-modern">{{ item.subscriber_id.name }}</span>
                                </div>
                            </div>
                        </v-expand-transition>

                        <!-- Modern audio controls with soft colors -->
                        <div v-if="item.type !== 'הודעה פרטית'" class="audio-controls-modern">
                            <v-btn icon x-small @click="togglePlay(item)" class="control-btn-modern play-btn-modern"
                                :class="{ 'active-play': item.isPlaying && !item.isPaused }">
                                <v-icon x-small>
                                    {{ item.isPlaying && !item.isPaused ? 'mdi-pause' : 'mdi-play' }}
                                </v-icon>
                            </v-btn>

                            <v-btn icon x-small @click="stop(item)" class="control-btn-modern stop-btn-modern"
                                :disabled="!item.isPlaying">
                                <v-icon x-small>mdi-stop</v-icon>
                            </v-btn>

                            <v-btn icon x-small @click="download(item)" class="control-btn-modern download-btn-modern">
                                <v-icon x-small>mdi-download</v-icon>
                            </v-btn>
                        </div>
                    </v-card-text>

                    <!-- Cleaner card actions -->
                    <v-card-actions class="card-actions-modern">
                        <v-btn icon x-small class="expand-btn-modern ml-3" @click="toggleExpand(item._id)">
                            <v-icon small>{{ expandedCards[item._id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>

                        <v-btn v-if="item.type !== 'הודעה פרטית'" icon x-small class="stats-btn-modern"
                            @click="openStatisticDialog(item, index)">
                            <v-icon small>mdi-chart-bar</v-icon>
                        </v-btn>

                        <v-spacer></v-spacer>

                        <v-btn icon x-small class="delete-btn-modern" @click="openDeleteDialog(item, index)">
                            <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>

                        <v-tooltip bottom v-if="item.type === 'ממתין לאישור מנהל'">
                            <template v-slot:activator="{ on }">
                                <v-btn icon x-small v-on="on" @click="forward(item)"
                                    class="control-btn forward-btn mr-3">
                                    <v-icon small>mdi-arrow-right-bold</v-icon>
                                </v-btn>
                            </template>
                            <span>העברה לשלוחת הודעות</span>
                        </v-tooltip>
                    </v-card-actions>
                </v-card>
            </div>

            <!-- Pagination for Grid View -->
            <div v-if="records.length > 0" class="pagination-section">
                <v-card class="pagination-card">
                    <v-card-text class="pagination-content">
                        <div class="pagination-info">
                            <span>{{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage,
                                totalItems) }} מתוך {{ totalItems.toLocaleString() }}</span>
                        </div>

                        <div class="pagination-controls">
                            <v-btn icon small @click="currentPage = 1; getRecords()" :disabled="currentPage === 1">
                                <v-icon>mdi-chevron-double-right</v-icon>
                            </v-btn>
                            <v-btn icon small @click="currentPage--; getRecords()" :disabled="currentPage === 1">
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-btn>

                            <span class="page-number">עמוד {{ currentPage }} מתוך {{ Math.ceil(totalItems /
                                itemsPerPage) }}</span>

                            <v-btn icon small @click="currentPage++; getRecords()"
                                :disabled="currentPage >= Math.ceil(totalItems / itemsPerPage)">
                                <v-icon>mdi-chevron-left</v-icon>
                            </v-btn>
                            <v-btn icon small @click="currentPage = Math.ceil(totalItems / itemsPerPage); getRecords()"
                                :disabled="currentPage >= Math.ceil(totalItems / itemsPerPage)">
                                <v-icon>mdi-chevron-double-left</v-icon>
                            </v-btn>
                        </div>

                        <v-select v-model="itemsPerPage" :items="[20, 50, 100, 500]" label="שורות בעמוד" dense outlined
                            hide-details class="items-per-page-select" @change="onItemsPerPageChange"></v-select>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Table Section -->
        <div class="table-section" v-else>
            <v-card class="table-card">
                <v-data-table :headers="headers" :items="records" v-model="selectedRecords" show-select item-key="_id"
                    class="recordings-table" :loading="progressShow" loading-text="טוען נתונים..."
                    no-data-text="אין עדיין הקלטות" :server-items-length="totalItems" :items-per-page="itemsPerPage"
                    :page.sync="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                    @update:page="onPageChange" @update:items-per-page="onItemsPerPageChange"
                    @update:sort-by="onSortChange" @update:sort-desc="onSortChange" :footer-props="{
                        'items-per-page-options': [20, 50, 100, 500], 'items-per-page-text': 'שורות בעמוד:', showFirstLastPage: true,
                        firstIcon: 'mdi-chevron-double-left',
                        lastIcon: 'mdi-chevron-double-right',
                        prevIcon: 'mdi-chevron-left',
                        nextIcon: 'mdi-chevron-right'
                    }">

                    <!-- select-all header -->
                    <template v-slot:header.data-table-select="{ on, props }">
                        <div>
                            <v-simple-checkbox v-bind="props" v-on="on" color="#0d2c6d"></v-simple-checkbox>
                        </div>
                    </template>

                    <!-- Custom Header -->
                    <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
                        v-slot:[`header.${header.value}`]="{ header }">
                        <div class="custom-header">{{ header.text }}</div>
                    </template>

                    <!-- Desktop View -->
                    <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index, isSelected, select }">
                        <tr class="table-row" :class="{
                            'playing-row': item.isPlaying,
                            'selected-row': isSelected
                        }">
                            <td class="checkbox-cell">
                                <v-simple-checkbox :value="isSelected" @input="select($event)"
                                    color="#0d2c6d"></v-simple-checkbox>
                            </td>

                            <td class="table-cell">
                                <div class="id-cell">
                                    {{ item.integer_id }}
                                    <v-icon v-if="item.isPlaying" small color="#0d2c6d" class="playing-indicator">
                                        mdi-volume-high
                                    </v-icon>
                                </div>
                            </td>

                            <td class="table-cell">{{ item?.subscriber_id?.name || '' }}</td>
                            <td class="table-cell">{{ item.caller_id }}</td>

                            <td class="table-cell">
                                <div class="type-cell">
                                    <span>{{ item.type }}</span>
                                    <v-tooltip bottom color="#0d2c6d" v-if="item.shared_from_ivr">
                                        <template v-slot:activator="{ on }">
                                            <v-icon small color="#0d2c6d" v-on="on" class="info-icon">
                                                mdi-information
                                            </v-icon>
                                        </template>
                                        <span>שותף ממערכת מספר: {{ item.shared_from_ivr }}</span>
                                    </v-tooltip>
                                </div>
                            </td>

                            <td class="table-cell date-cell">{{ item.createdAt }}</td>
                            <td class="table-cell date-cell">{{ item.customCreatedAt }}</td>
                            <td class="table-cell">{{ item.record_duration }}</td>
                            <td class="table-cell">{{ item.record_name_original }}</td>

                            <!-- Updated table buttons with original soft colors -->
                            <td class="table-cell">
                                <div class="audio-controls">
                                    <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="stop(item)"
                                                class="table-control-btn table-stop-btn"
                                                :class="{ 'table-active-control': item.isPlaying }">
                                                <v-icon small>mdi-stop</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>עצור</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="pause(item)"
                                                class="table-control-btn table-pause-btn"
                                                :class="{ 'table-active-control': item.isPlaying && item.isPaused }">
                                                <v-icon small>mdi-pause</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>הפסק</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="togglePlay(item)"
                                                class="table-control-btn table-play-btn"
                                                :class="{ 'table-active-control': item.isPlaying && !item.isPaused }">
                                                <v-icon small>
                                                    {{ item.isPlaying && !item.isPaused ? 'mdi-pause' : 'mdi-play' }}
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.isPlaying && !item.isPaused ? 'הפסק' : 'הפעל' }}</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="download(item)"
                                                class="table-control-btn table-download-btn">
                                                <v-icon small>mdi-download</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>הורדה</span>
                                    </v-tooltip>
                                </div>
                            </td>

                            <!-- Updated table action buttons with soft colors -->
                            <td class="table-cell">
                                <div class="action-buttons">
                                    <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="openStatisticDialog(item, index)"
                                                class="table-action-btn table-stats-btn">
                                                <v-icon small>mdi-chart-bar</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>סטטיסטיקת שמיעה</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="openDeleteDialog(item, index)"
                                                class="table-action-btn table-delete-btn">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>מחיקה</span>
                                    </v-tooltip>

                                    <v-tooltip bottom v-if="item.type === 'ממתין לאישור מנהל'">
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small v-on="on" @click="forward(item)"
                                                class="control-btn forward-btn">
                                                <v-icon small>mdi-arrow-right-bold</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>העברה לשלוחת הודעות</span>
                                    </v-tooltip>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <!-- Mobile View Actions -->
                    <template v-if="$vuetify.breakpoint.mdAndDown" v-slot:item.actions="{ item, index }">
                        <div class="mobile-actions">
                            <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="openStatisticDialog(item, index)"
                                        class="action-btn stats-btn">
                                        <v-icon small>mdi-chart-bar</v-icon>
                                    </v-btn>
                                </template>
                                <span>סטטיסטיקת שמיעה</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="openDeleteDialog(item, index)"
                                        class="action-btn delete-btn">
                                        <v-icon small>mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>מחיקה</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type === 'ממתין לאישור מנהל'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="forward(item)"
                                        class="control-btn forward-btn">
                                        <v-icon small>mdi-arrow-right-bold</v-icon>
                                    </v-btn>
                                </template>
                                <span>העברה לשלוחת הודעות</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <!-- Mobile View Record Controls -->
                    <template v-if="$vuetify.breakpoint.mdAndDown" v-slot:item.record_name="{ item }">
                        <div class="mobile-controls">
                            <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="stop(item)" class="control-btn stop-btn"
                                        :class="{ 'active-control': item.isPlaying }">
                                        <v-icon small>mdi-stop</v-icon>
                                    </v-btn>
                                </template>
                                <span>עצור</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="pause(item)" class="control-btn pause-btn"
                                        :class="{ 'active-control': item.isPlaying && item.isPaused }">
                                        <v-icon small>mdi-pause</v-icon>
                                    </v-btn>
                                </template>
                                <span>הפסק</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="togglePlay(item)" class="control-btn play-btn"
                                        :class="{ 'active-control': item.isPlaying && !item.isPaused }">
                                        <v-icon small>
                                            {{ item.isPlaying && !item.isPaused ? 'mdi-pause' : 'mdi-play' }}
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ item.isPlaying && !item.isPaused ? 'הפסק' : 'הפעל' }}</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.type !== 'הודעה פרטית'">
                                <template v-slot:activator="{ on }">
                                    <v-btn icon x-small v-on="on" @click="download(item)"
                                        class="control-btn download-btn">
                                        <v-icon small>mdi-download</v-icon>
                                    </v-btn>
                                </template>
                                <span>הורדה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <!-- Mobile View Type Column -->
                    <template v-if="$vuetify.breakpoint.mdAndDown" v-slot:item.type="{ item }">
                        <div class="type-cell">
                            <span>{{ item.type }}</span>
                            <v-tooltip bottom color="#0d2c6d" v-if="item.shared_from_ivr">
                                <template v-slot:activator="{ on }">
                                    <v-icon small color="#0d2c6d" v-on="on" class="info-icon">
                                        mdi-information
                                    </v-icon>
                                </template>
                                <span>שותף ממערכת מספר: {{ item.shared_from_ivr }}</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <!-- No Data Template -->
                    <template v-slot:no-data>
                        <div class="no-data-container">
                            <v-icon large color="#0d2c6d" class="mb-4">mdi-playlist-music-outline</v-icon>
                            <div class="no-data-text">אין עדיין הקלטות</div>
                            <v-btn color="#0d2c6d" outlined @click="refreshDetails()" class="refresh-btn mt-4">
                                <v-icon left>mdi-refresh</v-icon>
                                רענן
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </div>

        <!-- ============ Now Playing — modern floating player ============ -->
        <transition name="np-pop">
            <div v-if="currentlyPlaying" class="np-dock" :class="{ 'np-active': isActuallyPlaying }">
                <div class="np-glow"></div>

                <!-- header -->
                <div class="np-head">
                    <div class="np-cover" :class="{ 'np-cover-on': isActuallyPlaying }">
                        <span class="np-eq"><i></i><i></i><i></i><i></i></span>
                    </div>

                    <div class="np-meta">
                        <div class="np-title" :title="currentlyPlaying.record_name_original">
                            {{ currentlyPlaying.record_name_original || 'הקלטה' }}
                        </div>
                        <div class="np-sub">
                            <span class="np-chip">#{{ currentlyPlaying.integer_id }}</span>
                            <span class="np-dot"></span>
                            <span class="np-sub-txt">{{ currentlyPlaying.caller_id }}</span>
                        </div>
                    </div>

                    <button type="button" class="np-icon-btn" @click="stop(currentlyPlaying)" title="סגור">
                        <v-icon small>mdi-close</v-icon>
                    </button>
                </div>

                <!-- waveform + seek -->
                <div class="np-track-row">
                    <span class="np-time np-time-cur">{{ formatTime(audioCurrentTime) }}</span>

                    <div ref="wave" class="np-wave" tabindex="0" role="slider" :aria-valuemin="0"
                        :aria-valuemax="audioDuration" :aria-valuenow="audioCurrentTime"
                        @pointerdown="scrubStart" @pointermove="scrubMove" @pointerup="scrubEnd"
                        @pointercancel="scrubEnd" @pointerleave="onWaveLeave"
                        @keydown.left.prevent="skip(-5)" @keydown.right.prevent="skip(5)">

                        <div class="np-wave-layer np-wave-base">
                            <i v-for="(h, i) in waveBars" :key="'b' + i" :style="{ height: h + '%' }"></i>
                        </div>

                        <div class="np-wave-layer np-wave-fill">
                            <i v-for="(h, i) in waveBars" :key="'f' + i" :style="{ height: h + '%' }"></i>
                        </div>

                        <div class="np-playhead"></div>

                        <div v-if="hoverRatio !== null" class="np-hover-tip"
                            :style="{ left: (hoverRatio * 100) + '%' }">
                            {{ formatTime(hoverRatio * audioDuration) }}
                        </div>
                    </div>

                    <span class="np-time np-time-total">{{ formatTime(audioDuration) }}</span>
                </div>

                <!-- controls -->
                <div class="np-controls">
                    <div class="np-controls-side">
                        <button type="button" class="np-icon-btn" @click="cycleRate"
                            :title="'מהירות ניגון: ' + playbackRate + 'x'">
                            <span class="np-rate">{{ playbackRate }}x</span>
                        </button>

                        <div class="np-volume">
                            <button type="button" class="np-icon-btn" @click="toggleMute" title="עוצמת שמע">
                                <v-icon small>{{ volumeIcon }}</v-icon>
                            </button>
                            <input class="np-vol-range" type="range" min="0" max="1" step="0.01" :value="volume"
                                @input="setVolume($event.target.value)" title="עוצמת שמע" />
                        </div>
                    </div>

                    <div class="np-controls-main">
                        <button type="button" class="np-icon-btn np-skip" @click="skip(-15)" title="15 שניות אחורה">
                            <v-icon>mdi-rewind</v-icon>
                        </button>

                        <button type="button" class="np-play" @click="togglePlay(currentlyPlaying)"
                            :title="isActuallyPlaying ? 'השהה' : 'נגן'">
                            <v-icon>{{ isActuallyPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                        </button>

                        <button type="button" class="np-icon-btn np-skip" @click="skip(15)" title="15 שניות קדימה">
                            <v-icon>mdi-fast-forward</v-icon>
                        </button>
                    </div>

                    <div class="np-controls-side np-controls-end">
                        <button type="button" class="np-icon-btn" @click="stop(currentlyPlaying)" title="עצור">
                            <v-icon small>mdi-stop</v-icon>
                        </button>
                        <button type="button" class="np-icon-btn" @click="download(currentlyPlaying)" title="הורדה">
                            <v-icon small>mdi-download</v-icon>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Loading Overlay -->
        <v-overlay :value="overlay" class="loading-overlay">
            <v-progress-circular indeterminate size="64" width="4" color="#0d2c6d"></v-progress-circular>
        </v-overlay>

        <!-- Dialogs -->
        <DeleteItemDialog v-model="deleteDialog" v-if="deleteDialog" :item="itemToDelete" :items="itemsToDelete"
            :isBulkDelete="isBulkDelete" actionType="softDelete" @itemDeleted="itemDeleted"
            @itemsDeleted="itemsDeleted">
        </DeleteItemDialog>

        <AddRecordDialog v-model="addRecordDialog" v-if="addRecordDialog" @uploadComplete="recordAdded">
        </AddRecordDialog>

        <ListeningStatisticDialog v-model="listeningStatisticDialog" v-if="listeningStatisticDialog"
            :listeningDetails="listeningDetails" :recordDuration="recordDuration"></ListeningStatisticDialog>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </div>
</template>


<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import utils from '@/util/utils'
import Auth from '@/services/auth.service.js'
import DeleteItemDialog from '@/components/upload/dialogs/DeleteItemDialog'
import ListeningStatisticDialog from '@/components/upload/dialogs/ListeningStatisticDialog'
import AddRecordDialog from '@/components/upload/dialogs/AddRecordDialog'
import _ from 'lodash'

export default {
    components: {
        SnackBar,
        DeleteItemDialog,
        AddRecordDialog,
        ListeningStatisticDialog
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: 'green',
        snacbarText: '',
        lineNumber: '',
        progressShow: false,
        itemToDelete: {},
        itemsToDelete: [],
        isBulkDelete: false,
        selectedRecords: [],
        deleteDialog: false,
        listeningStatisticDialog: false,
        listeningDetails: [],
        recordDuration: '',
        addRecordDialog: false,
        overlay: false,
        search: '',
        records: [],
        currentlyPlaying: null,

        totalItems: 0,
        currentPage: 1,
        itemsPerPage: 20,
        sortBy: 'customCreatedAt',
        sortDesc: true,

        viewMode: 'grid',
        expandedCards: {},

        audioCurrentTime: 0,
        audioDuration: 0,
        isSeeking: false,
        hoverRatio: null,
        waveBars: [],
        playbackRate: 1,
        rateSteps: [1, 1.25, 1.5, 2, 0.75],
        volume: 1,
        lastVolume: 1,

        headers: [
            { text: "קובץ מס'", value: 'integer_id', align: 'center' },
            { text: 'שם', align: 'center', value: 'subscriber_id.name' },
            { text: 'טלפון', value: 'caller_id', align: 'center' },
            { text: 'שלוחה', value: 'type', align: 'center' },
            { text: 'תאריך הקלטה', value: 'createdAt', align: 'center' },
            { text: 'תאריך העלאה לשלוחה', value: 'customCreatedAt', align: 'center' },
            { text: 'זמן הקלטה', value: 'record_duration', align: 'center' },
            { text: 'שם הקלטה', value: 'record_name_original', align: 'center' },
            { text: 'שמע', value: 'record_name', align: 'center' },
            { text: 'פעולות', value: 'actions', sortable: false, align: 'center' }
        ]
    }),
    computed: {
        isActuallyPlaying() {
            return !!(this.currentlyPlaying && this.currentlyPlaying.isPlaying && !this.currentlyPlaying.isPaused)
        },

        volumeIcon() {
            if (this.volume === 0) return 'mdi-volume-off'
            if (this.volume < 0.5) return 'mdi-volume-medium'
            return 'mdi-volume-high'
        }
    },

    watch: {
        audioDuration() {
            this.$nextTick(() => this.paintProgress(this.audioCurrentTime))
        }
    },

    methods: {
        // בקשת רשומות מהשרת עם פייג’ינג, סורט וחיפוש
        async getRecords(newSearch = false) {
            try {
                if (newSearch) {
                    this.currentPage = 1
                }

                this.progressShow = true

                const query = new URLSearchParams({
                    page: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy || '',
                    sortDesc: this.sortDesc ? 'desc' : 'asc',
                    search: this.search || ''
                }).toString()

                const token = localStorage.getItem('token')
                const api =
                    process.env.VUE_APP_BASE_URL + `/records/get_records?${query}`

                const res = await fetch(
                    api,
                    ApiServices.requestOptions('GET', '', token)
                )

                const jsonObject = await res.json()
                this.progressShow = false

                if (res.status >= 400) {
                    this.showSnackBar('שגיאה בקבלת נתונים', 'red')
                } else if (res.status === 200) {
                    // מצופה פורמט: { data: [...], totalItems: number }
                    this.records = jsonObject.data || []
                    this.totalItems = jsonObject.totalItems || 0
                    this.formatDetails()
                }
            } catch (error) {
                this.progressShow = false
                this.showSnackBar('Error get files: ' + error, 'red')
            }
        },

        formatDetails() {
            this.records = this.records.map((record, index) => {
                return {
                    ...record,
                    customCreatedAt: utils.getCurrentDate(record.customCreatedAt),
                    createdAt: utils.getCurrentDate(record.createdAt),
                    record_duration_original: record.record_duration,
                    record_duration: this.formatDuration(record.record_duration),
                    type: this.getRecordTypeText(record.type, record.shared_from_ivr),
                    caller_id:
                        record.caller_id === 'WEBSITE'
                            ? 'אתר הניהול'
                            : record.caller_id,
                    position:
                        (this.currentPage - 1) * this.itemsPerPage + (index + 1),
                    isPlaying: false,
                    isPaused: false
                }
            })
        },

        formatDetailsSingle(record) {
            return {
                ...record,
                customCreatedAt: utils.getCurrentDate(record.customCreatedAt),
                createdAt: utils.getCurrentDate(record.createdAt),
                record_duration_original: record.record_duration,
                record_duration: this.formatDuration(record.record_duration),
                type: this.getRecordTypeText(record.type, record.shared_from_ivr),
                position: this.records.length + 1,
                caller_id: 'אתר הניהול',
                isPlaying: false,
                isPaused: false
            }
        },
        getRecordTypeText(type, shared = "") {
            const types = {
                regular: 'שלוחת הודעות',
                private: 'הודעה פרטית',
                managers: 'הודעה למנהלים',
                pending: 'הודעה מתוזמנת',
                shared: 'הודעה משותפת',
                survey: 'סקר',
                await_approval: 'ממתין לאישור מנהל',
            };
            if (shared) {
                return 'הודעה משותפת';
            }

            return types[type] || 'הודעה משותפת';
        },

        async setAudio(record) {
            try {
                const token = localStorage.getItem('token')
                const api =
                    process.env.VUE_APP_BASE_URL + `/` + record.record_name
                const res = await fetch(
                    api,
                    ApiServices.requestOptions('GET', '', token)
                )
                const jsonObject = await res.blob()

                if (res.status >= 400) {
                    this.showSnackBar('שגיאה בהפעלת ההקלטה', 'red')
                } else if (res.status === 200) {
                    const url = URL.createObjectURL(jsonObject)
                    record.record_link = url
                    record.record_audio = new Audio(url)
                    record.record_blob = jsonObject
                    const index = this.records.findIndex(
                        record1 => record1._id === record._id
                    )
                    Object.assign(this.records[index], record)
                }
            } catch (error) {
                console.error(error);
                this.showSnackBar('Error get files: ' + error, 'red')
            }
        },

        async setNewAudio(record) {
            try {
                const token = localStorage.getItem('token')
                const api =
                    process.env.VUE_APP_BASE_URL + `/` + record.record_name
                const res = await fetch(
                    api,
                    ApiServices.requestOptions('GET', '', token)
                )
                const jsonObject = await res.blob()

                if (res.status >= 400) {
                    // this.showSnackBar("שגיאה בהפעלת ההקלטה", "red");
                } else if (res.status === 200) {
                    const url = URL.createObjectURL(jsonObject)
                    record.record_link = url
                    record.record_audio = new Audio(url)
                    record.record_blob = jsonObject
                    record = this.formatDetailsSingle(record)
                    this.records.push(record)
                }
            } catch (error) {
                console.error(error);
                this.showSnackBar('Error get files: ' + error, 'red')
            }
        },

        formatDuration(seconds) {
            const hours = Math.floor(seconds / 3600)
            const minutes = Math.floor((seconds % 3600) / 60)
            const sec = seconds % 60

            const paddedHours = hours.toString().padStart(2, '0')
            const paddedMinutes = minutes.toString().padStart(2, '0')
            const paddedSeconds = sec.toString().padStart(2, '0')

            return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
        },

        showSnackBar(message, color) {
            this.snacbarText = message
            this.snackbarColorBt = color
            this.snackbar = true
        },

        /* ===================== Audio player ===================== */

        // מחבר את כל מאזיני האודיו פעם אחת בלבד לכל אלמנט Audio
        bindAudioEvents(item) {
            const audio = item.record_audio
            if (!audio || audio._npBound) return
            audio._npBound = true

            const syncDuration = () => {
                if (this.currentlyPlaying !== item) return
                if (isFinite(audio.duration) && audio.duration > 0) {
                    this.audioDuration = audio.duration
                    this.$nextTick(() => this.paintProgress(this.audioCurrentTime))
                }
            }

            audio.addEventListener('loadedmetadata', syncDuration)
            audio.addEventListener('durationchange', syncDuration)

            // מקור האמת לשעון: האלמנט עצמו. יורה ~4 פעמים בשנייה
            audio.addEventListener('timeupdate', () => {
                if (this.currentlyPlaying === item && !this.isSeeking) {
                    this.audioCurrentTime = audio.currentTime
                }
            })

            audio.addEventListener('play', () => {
                if (this.currentlyPlaying !== item) return
                item.isPaused = false
                item.isPlaying = true
                this.startAudioTracking()
            })

            audio.addEventListener('pause', () => {
                if (this.currentlyPlaying === item && !audio.ended) item.isPaused = true
            })

            audio.addEventListener('ended', () => this.stop(item))

            audio.addEventListener('error', () => {
                this.stop(item)
                this.showSnackBar('שגיאה: קובץ שמע לא תקין', 'red')
            })
        },

        async play(item) {
            if (!item) return

            if (this.currentlyPlaying && this.currentlyPlaying !== item) {
                this.stop(this.currentlyPlaying)
            }

            if (!item.record_audio) {
                this.overlay = true
                await this.setAudio(item)
                this.overlay = false
            }
            if (!item.record_audio) return

            const audio = item.record_audio
            this.bindAudioEvents(item)

            if (this.currentlyPlaying !== item) {
                this.currentlyPlaying = item
                this.audioCurrentTime = audio.currentTime || 0
                this.audioDuration =
                    isFinite(audio.duration) && audio.duration > 0
                        ? audio.duration
                        : Number(item.record_duration_original) || 0
                this.loadWaveform(item)
            }

            audio.playbackRate = this.playbackRate
            audio.volume = this.volume

            item.isPlaying = true
            item.isPaused = false

            try {
                await audio.play()
            } catch (error) {
                item.isPlaying = false
                this.showSnackBar('לא ניתן להפעיל את ההקלטה', 'red')
                return
            }

            this.startAudioTracking()
        },

        togglePlay(item) {
            if (!item) return
            if (item.isPlaying && !item.isPaused) this.pause(item)
            else this.play(item)
        },

        pause(item) {
            if (item && item.record_audio && item.isPlaying) {
                item.record_audio.pause()
                item.isPaused = true
                this.stopAudioTracking()
            }
        },

        stop(item) {
            if (!item) return

            if (item.record_audio) {
                item.record_audio.pause()
                try {
                    item.record_audio.currentTime = 0
                } catch (error) { /* ignore */ }
            }

            item.isPlaying = false
            item.isPaused = false

            if (this.currentlyPlaying === item) {
                this.stopAudioTracking()
                this.currentlyPlaying = null
                this.audioCurrentTime = 0
                this.audioDuration = 0
                this.hoverRatio = null
                this.isSeeking = false
                this.waveBars = []
            }
        },

        async download(item) {

            const DOWNLOAD_COOLDOWN_MS = 30 * 60 * 1000; // חצי שעה
            const LAST_DOWNLOAD_KEY = 'last_audio_download_at';

            const now = Date.now();
            const lastDownloadAt = Number(localStorage.getItem(LAST_DOWNLOAD_KEY));

            // בדיקת cooldown
            if (lastDownloadAt && now - lastDownloadAt < DOWNLOAD_COOLDOWN_MS) {
                const minutesLeft = Math.ceil(
                    (DOWNLOAD_COOLDOWN_MS - (now - lastDownloadAt)) / 60000
                );
                this.showSnackBar(`ניתן להוריד קובץ פעם ב־30 דקות בלבד. נסה שוב בעוד ${minutesLeft} דקות.`, 'red');
                return;
            }

            if (!item.record_audio) {
                this.overlay = true
                await this.setAudio(item)
                this.overlay = false
            }

            const downloadLink = document.createElement('a')
            downloadLink.href = item.record_link

            if (!item.record_name_original) {
                const startIndex = item.record_name.indexOf('users_records/')
                downloadLink.download = item.record_name
                    .substring(
                        startIndex + 'users_records/'.length
                    )
                    .replace(/\//g, '_')
            } else {
                downloadLink.download = item.record_name_original
            }

            document.body.appendChild(downloadLink)
            downloadLink.click()
            document.body.removeChild(downloadLink)

            // שמירת זמן ההורדה
            localStorage.setItem(LAST_DOWNLOAD_KEY, String(now));
        },

        openDeleteDialog(item, index) {
            this.itemToDelete = item
            this.isBulkDelete = false
            this.deleteDialog = true
        },

        openBulkDeleteDialog() {
            this.itemsToDelete = [...this.selectedRecords]
            this.isBulkDelete = true
            this.deleteDialog = true
        },

        itemDeleted(id, text, color) {
            if (id === '') {
                this.showSnackBar(text, color)
                return
            }

            const index = this.records.findIndex(record => record._id === id)

            if (this.records[index] === this.currentlyPlaying) {
                this.currentlyPlaying = null
            }

            this.records.splice(index, 1)
            this.showSnackBar(text, color)
        },

        itemsDeleted(ids, text, color) {
            if (!ids || ids.length === 0) {
                this.showSnackBar(text, color)
                return
            }

            ids.forEach(id => {
                const index = this.records.findIndex(record => record._id === id)
                if (index !== -1) {
                    if (this.records[index] === this.currentlyPlaying) {
                        this.currentlyPlaying = null
                    }
                    this.records.splice(index, 1)
                }
            })

            this.selectedRecords = []
            this.showSnackBar(text, color)
        },

        recordAdded(record, text, color) {
            // אחרי העלאה – נרענן מהשרת (כולל פייג’ינג)
            this.getRecords()
        },

        openStatisticDialog(item, index) {
            this.listeningDetails = item.listening
            this.recordDuration = item.record_duration_original
            this.listeningStatisticDialog = true
        },

        refreshDetails() {
            this.getRecords()
        },

        // ==== EVENTS של סרבר פייגינג (כמו בלקוחות) ====
        onPageChange(newPage) {
            this.currentPage = newPage
            this.getRecords()
        },

        onItemsPerPageChange(newItemsPerPage) {
            this.itemsPerPage = newItemsPerPage
            this.getRecords(true)
        },

        onSortChange() {
            this.debouncedGetRecords(true)
        },

        handleSearchInput() {
            if (this.search) {
                this.debouncedGetRecords(true)
            } else {
                this.getRecords(true)
            }
        },

        isSelected(item) {
            return this.selectedRecords.some(record => record._id === item._id)
        },

        toggleSelection(item) {
            const index = this.selectedRecords.findIndex(record => record._id === item._id)
            if (index > -1) {
                this.selectedRecords.splice(index, 1)
            } else {
                this.selectedRecords.push(item)
            }
        },

        toggleExpand(cardId) {
            this.$set(this.expandedCards, cardId, !this.expandedCards[cardId])
        },

        /* ---------- progress painting (ללא רינדור מחדש של Vue) ---------- */

        startAudioTracking() {
            this.stopAudioTracking()

            const tick = () => {
                const item = this.currentlyPlaying
                const audio = item && item.record_audio
                if (!audio) {
                    this._npRaf = null
                    return
                }

                if (!this.isSeeking) this.paintProgress(audio.currentTime)

                if (audio.paused || audio.ended) {
                    this._npRaf = null
                    return
                }
                this._npRaf = requestAnimationFrame(tick)
            }

            this._npRaf = requestAnimationFrame(tick)
        },

        stopAudioTracking() {
            if (this._npRaf) {
                cancelAnimationFrame(this._npRaf)
                this._npRaf = null
            }
        },

        // מעדכן משתנה CSS בלבד — הפס והסמן זזים חלק ב-60fps בלי לגעת ב-reactivity
        paintProgress(time) {
            const wave = this.$refs.wave
            if (!wave) return
            const duration = this.audioDuration || 0
            const ratio = duration > 0 ? Math.min(1, Math.max(0, time / duration)) : 0
            wave.style.setProperty('--np-progress', (ratio * 100).toFixed(3) + '%')
        },

        /* ---------- seek / scrub ---------- */

        ratioFromEvent(event) {
            const wave = this.$refs.wave
            if (!wave) return 0
            const rect = wave.getBoundingClientRect()
            if (!rect.width) return 0
            return Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
        },

        scrubStart(event) {
            if (!this.currentlyPlaying || !this.audioDuration) return
            this.isSeeking = true
            const wave = this.$refs.wave
            if (wave && wave.setPointerCapture) {
                try {
                    wave.setPointerCapture(event.pointerId)
                } catch (error) { /* ignore */ }
            }
            this.applyScrub(event)
        },

        scrubMove(event) {
            if (this.isSeeking) this.applyScrub(event)
            else if (this.audioDuration) this.hoverRatio = this.ratioFromEvent(event)
        },

        applyScrub(event) {
            const ratio = this.ratioFromEvent(event)
            const time = ratio * this.audioDuration
            this.hoverRatio = ratio
            this.audioCurrentTime = time
            this.paintProgress(time)
        },

        scrubEnd(event) {
            if (!this.isSeeking) return

            const time = this.ratioFromEvent(event) * this.audioDuration
            const audio = this.currentlyPlaying && this.currentlyPlaying.record_audio

            this.isSeeking = false
            this.audioCurrentTime = time
            this.paintProgress(time)

            if (audio) {
                try {
                    audio.currentTime = time
                } catch (error) { /* ignore */ }
            }

            const wave = this.$refs.wave
            if (wave && wave.releasePointerCapture) {
                try {
                    wave.releasePointerCapture(event.pointerId)
                } catch (error) { /* ignore */ }
            }
        },

        onWaveLeave() {
            if (!this.isSeeking) this.hoverRatio = null
        },

        skip(seconds) {
            const audio = this.currentlyPlaying && this.currentlyPlaying.record_audio
            if (!audio) return

            const duration = this.audioDuration || audio.duration || 0
            const next = Math.min(Math.max(0, audio.currentTime + seconds), Math.max(0, duration - 0.05))

            try {
                audio.currentTime = next
            } catch (error) { /* ignore */ }

            this.audioCurrentTime = next
            this.paintProgress(next)
        },

        /* ---------- rate / volume ---------- */

        cycleRate() {
            const index = this.rateSteps.indexOf(this.playbackRate)
            this.playbackRate = this.rateSteps[(index + 1) % this.rateSteps.length]
            const audio = this.currentlyPlaying && this.currentlyPlaying.record_audio
            if (audio) audio.playbackRate = this.playbackRate
        },

        setVolume(value) {
            const volume = Math.min(1, Math.max(0, Number(value)))
            this.volume = volume
            if (volume > 0) this.lastVolume = volume
            const audio = this.currentlyPlaying && this.currentlyPlaying.record_audio
            if (audio) audio.volume = volume
        },

        toggleMute() {
            this.setVolume(this.volume > 0 ? 0 : this.lastVolume || 1)
        },

        /* ---------- waveform ---------- */

        loadWaveform(item) {
            const BARS = 64

            if (item.record_peaks && item.record_peaks.length) {
                this.waveBars = item.record_peaks
            } else {
                this.waveBars = this.placeholderWave(item, BARS)
                this.decodeWaveform(item, BARS)
            }

            this.$nextTick(() => this.paintProgress(this.audioCurrentTime))
        },

        // צורת גל דטרמיניסטית לפי מזהה הרשומה — אותה הקלטה תיראה תמיד אותו דבר
        placeholderWave(item, bars) {
            const key = String(item._id || item.integer_id || item.record_name || 'record')
            let seed = 7
            for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) % 2147483647

            const result = []
            for (let i = 0; i < bars; i++) {
                seed = (seed * 1103515245 + 12345) % 2147483648
                const random = seed / 2147483648
                const envelope = Math.sin((i / (bars - 1)) * Math.PI) * 0.4 + 0.6
                result.push(Math.round((20 + random * 80) * envelope))
            }
            return result
        },

        // צורת גל אמיתית מתוך ה-blob (אופציונלי, נכשל בשקט וחוזר ל-placeholder)
        async decodeWaveform(item, bars) {
            try {
                const blob = item.record_blob
                const AudioCtx = window.AudioContext || window.webkitAudioContext
                if (!blob || !AudioCtx) return

                const context = new AudioCtx()
                const buffer = await context.decodeAudioData(await blob.arrayBuffer())
                const data = buffer.getChannelData(0)
                const block = Math.max(1, Math.floor(data.length / bars))

                const peaks = []
                for (let i = 0; i < bars; i++) {
                    let sum = 0
                    let count = 0
                    const start = i * block
                    for (let j = start; j < start + block && j < data.length; j += 8) {
                        sum += Math.abs(data[j])
                        count++
                    }
                    peaks.push(count ? sum / count : 0)
                }

                const max = Math.max.apply(null, peaks) || 1
                const shaped = peaks.map(peak => Math.max(10, Math.round(Math.sqrt(peak / max) * 100)))

                item.record_peaks = shaped
                item.record_blob = null

                if (this.currentlyPlaying === item) {
                    this.waveBars = shaped
                    this.$nextTick(() => this.paintProgress(this.audioCurrentTime))
                }

                if (context.close) context.close()
            } catch (error) {
                /* משאירים את צורת הגל הזמנית */
            }
        },

        formatTime(seconds) {
            const value = Number(seconds)
            if (!isFinite(value) || value < 0) return '00:00'

            const total = Math.floor(value)
            const hours = Math.floor(total / 3600)
            const minutes = Math.floor((total % 3600) / 60)
            const secs = total % 60

            const mm = minutes.toString().padStart(2, '0')
            const ss = secs.toString().padStart(2, '0')

            return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
        },
        async forward(item) {
            try {
                this.progressShow = true;
                const datesJSON = JSON.stringify({ record: item });
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/records/forward_waiting_approval_to_exten_1`;
                const res = await fetch(api, ApiServices.requestOptions("POST", datesJSON, token));

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בהעברת הקובץ", "red");
                } else if (res.status === 200) {
                    const index = this.records.findIndex(record => record._id == item._id);
                    this.records.splice(index, 1);
                    if (index !== -1) {
                        item.type = 'regular';
                        this.$set(this.records, index, item);
                    }
                    this.showSnackBar("הקובץ הועבר בהצלחה לשלוחת הודעות", "green");
                }
                this.progressShow = false;
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error forward file: " + error, "red");
            }
        },
    },
    created() {
        // לא reactive בכוונה — מזהה ה-requestAnimationFrame של סרגל ההתקדמות
        this._npRaf = null
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber()
        this.getRecords()
        this.debouncedGetRecords = _.debounce(this.getRecords, 700)
    },
    beforeDestroy() {
        if (this.currentlyPlaying) {
            this.stop(this.currentlyPlaying)
        }
        this.stopAudioTracking()
    }
}
</script>


<style scoped>
.recordings-manager {
    padding: 0 16px;
}

.header-section {
    margin-bottom: 24px;
}

.search-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.search-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
}

.search-and-count {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    flex: 1;
}

.search-field {
    max-width: 300px;
}

.records-count {
    display: flex;
    align-items: center;
    gap: 8px;
}

.count-label {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
}

/* Added styles for header actions container */
.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

/* Added styles for view toggle */
.view-toggle {
    border-radius: 8px;
    overflow: hidden;
}

/* Updated button colors to match original table styling */
.bulk-delete-btn {
    background-color: #f44336 !important;
    color: white !important;
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    transition: all 0.3s ease;
}

.bulk-delete-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(244, 67, 54, 0.3) !important;
}

.add-record-btn {
    background-color: #0d2c6d !important;
    color: white !important;
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    transition: all 0.3s ease;
}

.add-record-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(13, 44, 109, 0.3) !important;
}

/* Grid View Styles - Modern Design */
.grid-section {
    margin-bottom: 32px;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    gap: 16px;
}

.loading-text {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.6);
    margin: 0;
}

/* Modern card grid with cleaner spacing */
.recordings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
}

.recording-card {
    border-radius: 16px;
    border: 2px solid transparent;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    height: fit-content;
    background: #ffffff;
}

.recording-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(92, 107, 192, 0.15) !important;
    border-color: rgba(92, 107, 192, 0.2);
}

.playing-card {
    border-color: #5c6bc0;
    box-shadow: 0 8px 24px rgba(92, 107, 192, 0.25) !important;
}

.selected-card {
    border-color: rgba(92, 107, 192, 0.4);
    background-color: rgba(92, 107, 192, 0.02);
}

/* Modern card header with cleaner gradient */
.card-header-modern {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: linear-gradient(135deg, #102597 0%, #2e40a7c5 100%);
    color: white;
    min-height: 40px;
}

.modern-checkbox {
    margin: 0 !important;
    padding: 0 !important;
}

.modern-checkbox>>>.v-input__slot {
    margin: 0 !important;
    padding: 0 !important;
}

.modern-checkbox>>>.v-icon {
    color: white !important;
    font-size: 22px !important;
}

.card-id-modern {
    display: flex;
    align-items: center;
    gap: 8px;
}

.audio-icon-modern {
    font-size: 20px !important;
}

.id-number-modern {
    font-size: 14px;
    font-weight: 600;
}

.playing-pulse-modern {
    animation: pulse-modern 1.5s ease-in-out infinite;
}

@keyframes pulse-modern {

    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: 0.7;
        transform: scale(1.1);
    }
}

/* Cleaner card content */
.card-content-modern {
    padding: 14px !important;
}

.primary-info-modern {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.info-row-modern {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
}

.info-icon-modern {
    flex-shrink: 0;
}

.info-label-modern {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.5);
    min-width: 42px;
}

.info-value-modern {
    color: rgba(0, 0, 0, 0.87);
    font-weight: 500;
    flex: 1;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.expanded-details-modern {
    padding: 12px;
    margin-top: 10px;
    border-top: 1px solid rgba(92, 107, 192, 0.1);
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: linear-gradient(to bottom, rgba(92, 107, 192, 0.02), transparent);
    border-radius: 8px;
}

/* Modern audio controls with soft, transparent colors */
.audio-controls-modern {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    background: linear-gradient(135deg, rgba(92, 107, 192, 0.05), rgba(92, 107, 192, 0.02));
    border-radius: 12px;
    margin-top: 10px;
}

.control-btn-modern {
    width: 32px !important;
    height: 32px !important;
    border-radius: 50%;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.control-btn-modern:hover:not(:disabled) {
    transform: scale(1.15);
}

.control-btn-modern:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.play-btn-modern {
    background-color: rgba(76, 175, 80, 0.12) !important;
    color: #4caf50 !important;
}

.play-btn-modern:hover:not(:disabled) {
    background-color: rgba(76, 175, 80, 0.2) !important;
}

.play-btn-modern.active-play {
    background-color: rgba(255, 152, 0, 0.15) !important;
    color: #ff9800 !important;
}

.stop-btn-modern {
    background-color: rgba(244, 67, 54, 0.12) !important;
    color: #f44336 !important;
}

.stop-btn-modern:hover:not(:disabled) {
    background-color: rgba(244, 67, 54, 0.2) !important;
}

.download-btn-modern {
    background-color: rgba(33, 150, 243, 0.12) !important;
    color: #2196f3 !important;
}

.download-btn-modern:hover:not(:disabled) {
    background-color: rgba(33, 150, 243, 0.2) !important;
}

/* Modern card actions */
.card-actions-modern {
    padding: 8px 12px !important;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.02), transparent);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    min-height: 44px;
}

.expand-btn-modern {
    background-color: rgba(92, 107, 192, 0.08) !important;
    color: #5c6bc0 !important;
    transition: all 0.2s ease;
}

.expand-btn-modern:hover {
    background-color: rgba(92, 107, 192, 0.15) !important;
}

.stats-btn-modern {
    background-color: rgba(255, 152, 0, 0.08) !important;
    color: #ff9800 !important;
    transition: all 0.2s ease;
}

.stats-btn-modern:hover {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.delete-btn-modern {
    background-color: rgba(244, 67, 54, 0.08) !important;
    color: #f44336 !important;
    transition: all 0.2s ease;
}

.delete-btn-modern:hover {
    background-color: rgba(244, 67, 54, 0.15) !important;
}

/* Pagination Styles */
.pagination-section {
    margin-top: 24px;
}

.pagination-card {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.pagination-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 20px !important;
}

.pagination-info {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 500;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.page-number {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.8);
    padding: 0 12px;
}

.items-per-page-select {
    max-width: 120px;
}

/* Table buttons with soft, transparent colors like original */
.table-control-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.table-stop-btn {
    background-color: rgba(244, 67, 54, 0.08) !important;
    color: #f44336 !important;
}

.table-stop-btn:hover {
    background-color: rgba(244, 67, 54, 0.15) !important;
}

.table-stop-btn.table-active-control {
    background-color: rgba(244, 67, 54, 0.2) !important;
}

.table-pause-btn {
    background-color: rgba(255, 152, 0, 0.08) !important;
    color: #ff9800 !important;
}

.table-pause-btn:hover {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.table-pause-btn.table-active-control {
    background-color: rgba(255, 152, 0, 0.2) !important;
}

.table-play-btn {
    background-color: rgba(76, 175, 80, 0.08) !important;
    color: #4caf50 !important;
}

.table-play-btn:hover {
    background-color: rgba(76, 175, 80, 0.15) !important;
}

.table-play-btn.table-active-control {
    background-color: rgba(76, 175, 80, 0.2) !important;
}

.table-download-btn {
    background-color: rgba(33, 150, 243, 0.08) !important;
    color: #2196f3 !important;
}

.table-download-btn:hover {
    background-color: rgba(33, 150, 243, 0.15) !important;
}

.table-action-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.table-stats-btn {
    background-color: rgba(255, 152, 0, 0.08) !important;
    color: #ff9800 !important;
}

.table-stats-btn:hover {
    background-color: rgba(255, 152, 0, 0.15) !important;
}

.table-delete-btn {
    background-color: rgba(244, 67, 54, 0.08) !important;
    color: #f44336 !important;
}

.table-delete-btn:hover {
    background-color: rgba(244, 67, 54, 0.15) !important;
}

/* Table Section Styles */
.table-section {
    margin-bottom: 32px;
}

.table-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.recordings-table {
    width: 100%;
}

.custom-header {
    font-size: 14px;
    font-weight: 600;
    color: #0d2c6d;
    text-align: center;
}

.table-row {
    transition: all 0.3s ease;
}

.table-row:hover {
    background-color: rgba(13, 44, 109, 0.03) !important;
}

.playing-row {
    background-color: rgba(13, 44, 109, 0.08) !important;
    border-left: 4px solid #0d2c6d;
}

.playing-row:hover {
    background-color: rgba(13, 44, 109, 0.12) !important;
}

.selected-row {
    background-color: rgba(13, 44, 109, 0.05) !important;
}

.table-cell {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.8);
    text-align: center !important;
    padding: 12px 8px !important;
}

.checkbox-cell {
    width: 48px;
    padding: 8px !important;
}

.id-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.playing-indicator {
    animation: pulse 1.5s ease-in-out infinite alternate;
}

@keyframes pulse {
    from {
        opacity: 0.6;
    }

    to {
        opacity: 1;
    }
}

.type-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.audio-controls,
.action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.mobile-controls,
.mobile-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

/* ===================== Now Playing — modern floating player ===================== */
.np-dock {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1000;
    width: 430px;
    max-width: calc(100vw - 32px);
    padding: 16px 18px 14px;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.82);
    -webkit-backdrop-filter: blur(22px) saturate(170%);
    backdrop-filter: blur(22px) saturate(170%);
    border: 1px solid rgba(255, 255, 255, 0.75);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.9),
        0 20px 55px -14px rgba(13, 44, 109, 0.42),
        0 4px 14px rgba(13, 44, 109, 0.12);
    overflow: hidden;
}

.np-glow {
    position: absolute;
    top: -90px;
    left: -20%;
    right: -20%;
    height: 200px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.5s ease;
    background: radial-gradient(60% 100% at 50% 0%, rgba(92, 107, 192, 0.38), transparent 70%);
}

.np-active .np-glow {
    opacity: 1;
    animation: npGlow 4s ease-in-out infinite;
}

@keyframes npGlow {

    0%,
    100% {
        transform: translateY(0);
        opacity: 0.7;
    }

    50% {
        transform: translateY(12px);
        opacity: 1;
    }
}

/* ---- header ---- */
.np-head {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
}

.np-cover {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #102597 0%, #5c6bc0 100%);
    box-shadow: 0 8px 18px -6px rgba(16, 37, 151, 0.7);
}

.np-eq {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 18px;
}

.np-eq i {
    width: 3px;
    height: 5px;
    border-radius: 2px;
    background: #fff;
    opacity: 0.92;
}

.np-cover-on .np-eq i {
    animation: npBar 0.9s ease-in-out infinite;
}

.np-cover-on .np-eq i:nth-child(2) {
    animation-delay: 0.15s;
}

.np-cover-on .np-eq i:nth-child(3) {
    animation-delay: 0.3s;
}

.np-cover-on .np-eq i:nth-child(4) {
    animation-delay: 0.45s;
}

@keyframes npBar {

    0%,
    100% {
        height: 5px;
    }

    50% {
        height: 17px;
    }
}

.np-meta {
    flex: 1;
    min-width: 0;
}

.np-title {
    font-size: 14px;
    font-weight: 700;
    color: #12204a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.np-sub {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 3px;
    min-width: 0;
}

.np-chip {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 700;
    color: #3f51b5;
    background: rgba(92, 107, 192, 0.13);
    border-radius: 999px;
    padding: 1px 8px;
}

.np-dot {
    flex: 0 0 auto;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: rgba(18, 32, 74, 0.3);
}

.np-sub-txt {
    font-size: 12px;
    color: rgba(18, 32, 74, 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ---- waveform ---- */
.np-track-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
    direction: ltr;
}

.np-time {
    min-width: 40px;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: rgba(18, 32, 74, 0.5);
}

.np-time-cur {
    color: #102597;
}

.np-wave {
    --np-progress: 0%;
    position: relative;
    flex: 1;
    height: 40px;
    cursor: pointer;
    outline: none;
    touch-action: none;
}

.np-wave-layer {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    gap: 2px;
    pointer-events: none;
}

.np-wave-layer i {
    flex: 1 1 0;
    min-width: 2px;
    border-radius: 2px;
    transition: height 0.3s ease, background 0.2s ease;
}

.np-wave-base i {
    background: rgba(18, 32, 74, 0.15);
}

.np-wave:hover .np-wave-base i {
    background: rgba(18, 32, 74, 0.24);
}

.np-wave-fill {
    clip-path: inset(0 calc(100% - var(--np-progress)) 0 0);
}

.np-wave-fill i {
    background: linear-gradient(180deg, #5c6bc0 0%, #102597 100%);
}

.np-playhead {
    position: absolute;
    top: 50%;
    left: var(--np-progress);
    width: 3px;
    height: 30px;
    border-radius: 2px;
    background: #fff;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 1.5px #102597, 0 2px 8px rgba(16, 37, 151, 0.45);
    pointer-events: none;
}

.np-hover-tip {
    position: absolute;
    bottom: calc(100% + 6px);
    transform: translateX(-50%);
    padding: 3px 8px;
    border-radius: 8px;
    background: #12204a;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.np-hover-tip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #12204a;
}

/* ---- controls ---- */
.np-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 12px;
}

.np-controls-side {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1;
    min-width: 0;
}

.np-controls-end {
    justify-content: flex-end;
}

.np-controls-main {
    display: flex;
    align-items: center;
    gap: 10px;
}

.np-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 12px;
    background: transparent;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
}

.np-icon-btn:hover {
    background: rgba(92, 107, 192, 0.14);
    transform: translateY(-1px);
}

.np-icon-btn:active {
    transform: translateY(0) scale(0.94);
}

.np-icon-btn .v-icon {
    color: #3f51b5 !important;
}

.np-rate {
    font-size: 11px;
    font-weight: 800;
    color: #3f51b5;
}

.np-play {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 54px;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    background: linear-gradient(135deg, #102597 0%, #5c6bc0 100%);
    box-shadow: 0 10px 24px -8px rgba(16, 37, 151, 0.85);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.np-play .v-icon {
    color: #fff !important;
    font-size: 28px;
}

.np-play:hover {
    transform: scale(1.06);
    box-shadow: 0 14px 30px -8px rgba(16, 37, 151, 0.95);
}

.np-play:active {
    transform: scale(0.97);
}

.np-active .np-play::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 2px solid rgba(92, 107, 192, 0.55);
    animation: npPulse 2s ease-out infinite;
    pointer-events: none;
}

@keyframes npPulse {
    0% {
        transform: scale(0.9);
        opacity: 0.8;
    }

    100% {
        transform: scale(1.35);
        opacity: 0;
    }
}

.np-volume {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
}

.np-vol-range {
    -webkit-appearance: none;
    appearance: none;
    width: 0;
    opacity: 0;
    height: 4px;
    border-radius: 4px;
    direction: ltr;
    background: rgba(18, 32, 74, 0.18);
    cursor: pointer;
    transition: width 0.25s ease, opacity 0.25s ease;
}

.np-volume:hover .np-vol-range,
.np-vol-range:focus {
    width: 70px;
    opacity: 1;
}

.np-vol-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #102597;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.np-vol-range::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: none;
    border-radius: 50%;
    background: #102597;
    cursor: pointer;
}

/* ---- enter / leave ---- */
.np-pop-enter-active {
    transition: opacity 0.38s cubic-bezier(0.22, 1, 0.36, 1), transform 0.38s cubic-bezier(0.22, 1, 0.36, 1);
}

.np-pop-leave-active {
    transition: opacity 0.22s ease-in, transform 0.22s ease-in;
}

.np-pop-enter,
.np-pop-leave-to {
    opacity: 0;
    transform: translateY(24px) scale(0.96);
}

@media (prefers-reduced-motion: reduce) {

    .np-glow,
    .np-cover-on .np-eq i,
    .np-active .np-play::after {
        animation: none !important;
    }
}

.loading-overlay {
    z-index: 1000;
}

.no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
}

.no-data-text {
    font-size: 18px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 16px;
}

.refresh-btn {
    text-transform: none;
    letter-spacing: 0;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.refresh-btn:hover {
    background-color: rgba(13, 44, 109, 0.05);
    transform: translateY(-2px);
}


.forward-btn {
    background-color: rgba(255, 152, 0, 0.1);
}

.control-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
}

.control-btn:hover {
    background-color: rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}


/* Responsive adjustments */
@media (max-width: 960px) {
    .search-container {
        flex-direction: column;
        align-items: stretch;
    }

    .search-and-count {
        flex-direction: column;
        align-items: stretch;
    }

    .search-field {
        max-width: 100%;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .view-toggle {
        order: -1;
        width: 100%;
        justify-content: center;
    }

    .bulk-delete-btn,
    .add-record-btn {
        flex: 1;
    }

    .recordings-grid {
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }

    .pagination-content {
        flex-direction: column;
        align-items: stretch;
    }

    .pagination-controls {
        justify-content: center;
    }

    .items-per-page-select {
        max-width: 100%;
    }

    .np-dock {
        right: 12px;
        left: 12px;
        bottom: 12px;
        width: auto;
        max-width: none;
    }
}

@media (max-width: 600px) {
    .recordings-manager {
        padding: 0 8px;
    }

    .recordings-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .np-dock {
        padding: 14px 14px 12px;
    }

    .np-wave {
        height: 34px;
    }

    .np-vol-range {
        display: none;
    }

    .np-controls-side {
        gap: 0;
    }
}

.v-data-table>>>td {
    text-align: center !important;
}

.v-data-table>>>th {
    background-color: rgba(0, 0, 0, 0.116) !important;
    border-left: 1px solid white !important;
}

.v-data-table>>>.v-data-table__wrapper table {
    table-layout: fixed;
}
</style>